import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export interface NavItemProps {
  to: string;
  label: string;
  icon: LucideIcon;
  /** The current page: carries `aria-current` and the bold weight. */
  active?: boolean;
  /** A descendant is the current page: the row is highlighted, but claims nothing. */
  inActiveGroup?: boolean;
  /** Nested entry: shorter row, indented, smaller label. */
  indented?: boolean;
  /** Renders the group chevron next to the link. */
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-white dark:focus-visible:ring-zinc-100 dark:focus-visible:ring-offset-zinc-950';

const NavItem = ({
  to,
  label,
  icon: Icon,
  active = false,
  inActiveGroup = false,
  indented = false,
  collapsible = false,
  expanded = false,
  onToggle,
  onNavigate,
}: NavItemProps) => {
  const { t } = useTranslation();
  // The highlight lives on the row, not on the link, so it runs under the chevron too.
  const highlighted = active || inActiveGroup;

  return (
    <div
      className={cn(
        'group flex items-center rounded-lg transition-colors',
        highlighted ? 'bg-zinc-100 dark:bg-zinc-800' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800',
      )}
    >
      <Link
        to={to}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-3 transition-colors',
          FOCUS_RING,
          indented ? 'h-8 pl-[39px] text-[13px]' : 'h-9 text-sm',
          active && 'font-semibold',
          highlighted
            ? 'text-zinc-900 dark:text-zinc-50'
            : 'text-zinc-500 group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-50',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </Link>
      {collapsible && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t('Collapse {{section}}', { section: label })
              : t('Expand {{section}}', { section: label })
          }
          className={cn(
            'flex h-9 w-6 shrink-0 items-center justify-center rounded-lg transition-colors',
            FOCUS_RING,
            highlighted
              ? 'text-zinc-500 dark:text-zinc-400'
              : 'text-zinc-400 group-hover:text-zinc-900 dark:text-zinc-500 dark:group-hover:text-zinc-50',
          )}
        >
          <ChevronRight
            className={cn('h-4 w-4 transition-transform duration-150', expanded && 'rotate-90')}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
};

export default NavItem;
